<template>
  <div class="ms" :data-channel="channel" data-test="message-settings">
    <NoticeBox
      v-if="notice"
      :title="notice.title"
      :link-label="notice.canFix ? 'Open Settings' : ''"
      :link-href="settingsHref"
    >{{ notice.text }}</NoticeBox>

    <div
      class="ms__stack"
      :class="{ 'ms__stack--off': off }"
      :aria-disabled="off ? 'true' : null"
      :inert="off ? '' : null"
      data-test="message-settings-body"
    >
      <SettingsCard v-if="isEmail" title="Sender">
        <FieldRow label="From name" help="Set from your practice name in Settings.">
          <div class="ms__ro" title="From name is your site's name (configured in site settings)." data-test="email-from-name">
            <WfIcon name="lock" :size="14" class="ms__ro-icon" />
            <span>{{ wfBuilder.currentSiteName }}</span>
          </div>
        </FieldRow>
        <FieldRow
          label="From email address"
          help="Set from your practice’s verified sender email. Ask your admin to change it."
        >
          <div
            class="ms__ro"
            :class="{ 'ms__ro--warn': !wfBuilder.firstVerifiedEmail }"
            title="From email is your site's verified sender address (GENERAL_VERIFIED_EMAILS in site settings)."
            data-test="email-from-address"
          >
            <WfIcon :name="wfBuilder.firstVerifiedEmail ? 'lock' : 'alert'" :size="14" class="ms__ro-icon" />
            <span>{{ wfBuilder.firstVerifiedEmail || 'Not configured — contact admin to verify a sender email.' }}</span>
          </div>
        </FieldRow>
      </SettingsCard>

      <!-- For new patients only: classic shows it under the Appointment Scheduled trigger only. -->
      <template v-if="wfBuilder.currentTrigger === 'APPOINTMENT_SCHEDULED'">
        <SectionHead
          title="For new patients only"
          :description="`The ${noun} only goes to patients who book as a new patient.`"
          :spaced="isEmail"
        />
        <SettingsCard data-test="new-patient-card">
          <FieldRow label="Send to" :choice="true">
            <div class="ms__choices" role="radiogroup" aria-label="Send to">
              <label
                v-for="option in NP_MODES"
                :key="option.value"
                class="ms__choice"
                :class="{ 'is-on': p[m.npMode] === option.value }"
              >
                <input
                  v-model="p[m.npMode]"
                  type="radio"
                  :value="option.value"
                  :name="`np-mode-${channel}`"
                  :data-test="`np-mode-${option.value}`"
                />
                <span class="ms__choice-text">
                  <span class="ms__choice-label">{{ option.label }}</span>
                  <span class="ms__choice-desc">{{ option.desc }}</span>
                </span>
              </label>
            </div>
          </FieldRow>

          <template v-if="p[m.npMode] === 'all'">
            <FieldRow label="Template">
              <WfPicker
                v-model="p[m.npTemplateObj]"
                :options="wfBuilder[m.templates]"
                :searchable="true"
                :show-labels="false"
                label="Name"
                track-by="WikiPageID"
                placeholder="Select template…"
                @input="(val) => p[m.npTemplateId] = val ? val[m.templateIdField] : ''"
              />
            </FieldRow>
            <FieldRow label="Providers" help="Leave empty for any provider.">
              <WfPicker
                v-model="p[m.npProviderObjs]"
                :options="wfBuilder.providersList"
                :multiple="true"
                :searchable="true"
                :close-on-select="false"
                :show-labels="false"
                label="displayName"
                track-by="ProvNum"
                placeholder="Any provider"
                @input="(vals) => p[m.npProviderNums] = (vals || []).map(v => v.ProvNum)"
              />
            </FieldRow>
          </template>
        </SettingsCard>

        <template v-if="p[m.npMode] === 'procedure_specific'">
          <RuleCard
            v-for="(rule, index) in npRules"
            :key="ruleKey(rule)"
            :title="`New patient rule ${index + 1}`"
            :summary="newPatientRuleSummary(rule)"
            :empty="isRuleEmpty(rule, 'newpatient')"
            :open="ruleOpen(rule, npRules.length)"
            data-kind="new-patient"
            @toggle="toggleRule(rule, npRules.length)"
            @remove="wfBuilder[m.removeNp](index)"
          >
            <FieldRow label="Proc codes">
              <WfPicker
                v-model="rule.procCodes"
                :options="wfBuilder.procCodesList"
                :multiple="true"
                :searchable="true"
                :close-on-select="false"
                :show-labels="false"
                label="text"
                track-by="value"
                placeholder="Select proc codes…"
                :proc-codes="true"
              />
            </FieldRow>
            <FieldRow label="Providers">
              <WfPicker
                v-model="rule.providerObjs"
                :options="wfBuilder.providersList"
                :multiple="true"
                :searchable="true"
                :close-on-select="false"
                :show-labels="false"
                label="displayName"
                track-by="ProvNum"
                placeholder="Any provider"
                @input="(vals) => rule.providerNums = (vals || []).map(v => v.ProvNum)"
              />
            </FieldRow>
            <FieldRow label="Template">
              <WfPicker
                v-model="rule.templateObj"
                :options="wfBuilder[m.templates]"
                :searchable="true"
                :show-labels="false"
                label="Name"
                track-by="WikiPageID"
                placeholder="Select template…"
                @input="(val) => rule.templateId = val ? val[m.templateIdField] : ''"
              />
            </FieldRow>
          </RuleCard>
          <button type="button" class="ms__add" data-test="new-patient-rule-add" @click="addNpRule">
            <WfIcon name="plus" :size="16" />
            Add new patient rule
          </button>
        </template>

        <div class="ms__divider" role="separator" data-test="new-patient-divider"></div>
      </template>

      <SectionHead
        :title="rulesTitle"
        :count="rules.length"
        :description="rulesDescription"
        :action-label="rules.length > 1 ? (allRulesOpen(rules) ? 'Collapse all' : 'Expand all') : ''"
        :spaced="isEmail || wfBuilder.currentTrigger === 'APPOINTMENT_SCHEDULED'"
        @action="toggleAllRules(rules)"
      />

      <RuleCard
        v-for="(rule, index) in rules"
        :key="ruleKey(rule)"
        :title="`Rule ${index + 1}`"
        :summary="messageRuleSummary(rule)"
        :empty="isRuleEmpty(rule, channel)"
        :open="ruleOpen(rule, rules.length)"
        :flag="ruleBlock(index) ? TOO_LONG : ''"
        data-kind="message"
        @toggle="toggleRule(rule, rules.length)"
        @remove="wfBuilder[m.remove](index)"
      >
        <FieldRow label="Proc codes">
          <WfPicker
            v-model="rule.procCodes"
            :options="wfBuilder.procCodesList"
            :multiple="true"
            :searchable="true"
            :close-on-select="false"
            :show-labels="false"
            label="text"
            track-by="value"
            placeholder="Select proc codes…"
            :proc-codes="true"
          />
        </FieldRow>
        <FieldRow label="Template" :note="templateNote(index)" :note-tone="templateTone(index)">
          <WfPicker
            v-model="rule.templateObj"
            :options="wfBuilder[m.templates]"
            :searchable="true"
            :show-labels="false"
            label="Name"
            track-by="WikiPageID"
            placeholder="Select template…"
            @input="(val) => onTemplatePick(rule, val)"
          />
          <template #note-extra>
            <span
              v-if="noteExtra(index, rule) === 'pick'"
              class="ms__note-hint"
              data-test="rule-pick-template-hint"
            >Pick a template to replace it.</span>
            <button
              v-else-if="noteExtra(index, rule) === 'clear'"
              type="button"
              class="ms__note-btn"
              data-test="rule-clear-old-text"
              @click="clearOldText(rule)"
            >Clear the old text</button>
          </template>
        </FieldRow>
        <FieldRow label="Send when">
          <WfPicker
            v-model="rule.sendWhenObj"
            :options="wfBuilder[m.sendOptions]"
            :searchable="false"
            :show-labels="false"
            label="text"
            track-by="value"
            placeholder="Select when…"
            :max-height="320"
            @input="(val) => rule.sendWhen = val ? val.value : 'immediately'"
          />
        </FieldRow>
      </RuleCard>

      <button type="button" class="ms__add" data-test="message-rule-add" @click="addMessageRule">
        <WfIcon name="plus" :size="16" />
        {{ isEmail ? 'Add email rule' : 'Add text rule' }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Send text message / Send email (design frames 1i, 1j, 1p): ONE component, because the two panels are the
 * same panel with different property names. The table below is the whole difference and it is EXACTLY the
 * classic builder's (WorkflowBuilder.vue: the Email Settings and SMS Settings blocks):
 *
 *   - the rules live in `emailRules` / `smsRules`, with `emailSendOptions` / `smsSendOptions` and
 *     `emailTemplates` / `smsTemplates`;
 *   - a chosen template writes `templateId` from `SendgridTemplateID` (email) or `WikiPageID` (text);
 *   - "For new patients only" has its own mode, template, providers and rules per channel
 *     (`newPatientEmailMode` / `newPatientMode`, ...), shown under the Appointment Scheduled trigger only;
 *   - the add / remove methods are the classic ones, by name.
 *
 * WHO THE RULES ARE FOR (Appointment Scheduled only). A divider follows "For new patients only", and the rules
 * heading says "for all other patients" once that section is in use, "for all patients" while it is not: the
 * sender handles a new patient in that section and never reaches the rules (`newPatientsSkipRules`).
 *
 * "Not set up" notice, with the classic conditions: email without a verified sender, texting without a
 * number, and a WhatsApp-only office (`smsOutboundOff`: it has a number, but may not SEND texts). The body
 * is dimmed and inert while the notice shows (classic's `feature-disabled`). The two "isn't set up" notices
 * carry an "Open Settings" link (a new tab, `wfBuilder.settingsHref`; no link when the page has none). The
 * WhatsApp-only "Texting is turned off" notice carries none: that office has a number and chose WhatsApp, so
 * there is nothing in Settings for it to change.
 *
 * TOO-LONG TEXT (frame 1r, texts only). The workflow-level Save is blocked by `collectWorkflowSmsIssues`
 * (libraries/validate-workflow-sms.js, over the builder's committed nodes). The sheet runs the SAME library over
 * the node being edited (`selectedNodeData`, a clone), live, and each entry names its rule by `ruleIndex`
 * (an index into `smsRules`): a blocking entry puts a red "Too long to send" badge on that RuleCard (folded or
 * open) and the library's own message in red under its Template; a warning entry puts the message in amber
 * under the Template, nothing else. A flagged rule starts open when the sheet opens. Saving the block stays
 * allowed. The library measures `smsRules[].smsText` only, so the "For new patients only" template and rules
 * (which carry a template id and no text) are never flagged.
 *
 * THE OLD SAVED TEXT. No builder has a field for `smsText`, and the sender ignores it whenever the rule has a
 * template (utility `_sendSMS`: the wiki template when `smsTemplateID` is set, the saved text only when there is
 * none). So picking a template on a text rule drops that rule's saved text (`clearOldText`; a rule with no
 * `smsText` key stays without one, and clearing the template leaves it alone), and a flagged rule that already
 * has a template gets a "Clear the old text" button in its note; one without a template is told to pick a
 * template. This is the redesigned sheet only: the classic builder is unchanged. Texts only (`isEmail` returns).
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import FieldRow from './FieldRow.vue';
import NoticeBox from './NoticeBox.vue';
import RuleCard from './RuleCard.vue';
import SectionHead from './SectionHead.vue';
import SettingsCard from './SettingsCard.vue';
import WfPicker from './WfPicker.vue';
import WfIcon from '../WfIcon.vue';
import { ruleFold } from './ruleFold';
import { messageRuleSummary, newPatientRuleSummary, isRuleEmpty } from '@/utils/workflow/ruleSummary';
import { collectWorkflowSmsIssues } from '@/pages/Workflow2/libraries/validate-workflow-sms';

const TOO_LONG = 'Too long to send';
// Shown only if the library hands back an issue with no message of its own.
const TOO_LONG_FALLBACK = 'This text is too long to send.';
const NEAR_LIMIT_FALLBACK = 'This text is close to the length limit.';

/** { [ruleIndex]: issue }: the first issue for each rule of the node. */
function byRule(issues) {
  const out = {};
  issues.forEach((issue) => {
    if (issue && !Object.prototype.hasOwnProperty.call(out, issue.ruleIndex)) out[issue.ruleIndex] = issue;
  });
  return out;
}

const CHANNELS = {
  sms: {
    noun: 'text',
    rules: 'smsRules',
    sendOptions: 'smsSendOptions',
    templates: 'smsTemplates',
    templateIdField: 'WikiPageID',
    npMode: 'newPatientMode',
    npTemplateObj: 'newPatientSmsTemplateObj',
    npTemplateId: 'newPatientSmsTemplateId',
    npProviderObjs: 'newPatientProviderObjs',
    npProviderNums: 'newPatientProviderNums',
    npRules: 'newPatientRules',
    add: 'addSmsRule',
    remove: 'removeSmsRule',
    addNp: 'addNewPatientSmsRule',
    removeNp: 'removeNewPatientSmsRule',
  },
  email: {
    noun: 'email',
    rules: 'emailRules',
    sendOptions: 'emailSendOptions',
    templates: 'emailTemplates',
    templateIdField: 'SendgridTemplateID',
    npMode: 'newPatientEmailMode',
    npTemplateObj: 'newPatientEmailTemplateObj',
    npTemplateId: 'newPatientEmailTemplateId',
    npProviderObjs: 'newPatientEmailProviderObjs',
    npProviderNums: 'newPatientEmailProviderNums',
    npRules: 'newPatientEmailRules',
    add: 'addEmailRule',
    remove: 'removeEmailRule',
    addNp: 'addNewPatientEmailRule',
    removeNp: 'removeNewPatientEmailRule',
  },
};

const NP_MODES = [
  { value: 'all', label: 'All new patients', desc: 'Regardless of procedure' },
  { value: 'procedure_specific', label: 'Specific procedures', desc: 'Pick a template by procedure and provider' },
];

export default {
  name: 'MessageSettings',
  components: { FieldRow, NoticeBox, RuleCard, SectionHead, SettingsCard, WfPicker, WfIcon },
  mixins: [ruleFold],
  inject: ['wfBuilder'],

  props: {
    channel: { type: String, required: true, validator: v => v === 'sms' || v === 'email' },
  },

  data() {
    return { NP_MODES, TOO_LONG };
  },

  created() {
    // Opening on a block that already has a text too long to send: that rule starts open, so the problem is
    // on screen. (The fold state is this panel's own; a later toggle is respected.)
    Object.keys(this.smsIssues.blocks).forEach((index) => {
      const rule = this.rules[Number(index)];
      if (rule) this.$set(this.foldState, this.ruleKey(rule), true);
    });
  },

  computed: {
    m() {
      return CHANNELS[this.channel];
    },
    isEmail() {
      return this.channel === 'email';
    },
    noun() {
      return this.m.noun;
    },
    p() {
      return (this.wfBuilder.selectedNodeData || {}).properties || {};
    },
    rules() {
      return this.p[this.m.rules] || [];
    },
    npRules() {
      return this.p[this.m.npRules] || [];
    },
    hasNewPatientSection() {
      return this.wfBuilder.currentTrigger === 'APPOINTMENT_SCHEDULED';
    },
    // Whether a NEW patient skips the rules below, as the sender decides it (teamio-utility `sendSMS` and the
    // email path, the same two branches): 'procedure_specific' with at least one new patient rule (matched or
    // not), or a new patient template. Either way the sender returns before the proc-code rules. With neither,
    // a new patient falls through to the rules like everyone else.
    newPatientsSkipRules() {
      if (!this.hasNewPatientSection) return false;
      if (this.p[this.m.npMode] === 'procedure_specific' && this.npRules.length > 0) return true;
      return !!this.p[this.m.npTemplateId];
    },
    // Who the rules are for. Only said where there is a "For new patients only" section to tell them from.
    rulesTitle() {
      const base = this.isEmail ? 'Email rules' : 'Text rules';
      if (!this.hasNewPatientSection) return base;
      return `${base} for ${this.newPatientsSkipRules ? 'all other patients' : 'all patients'}`;
    },
    rulesDescription() {
      const how = 'Choose which template to send, based on the procedure codes.';
      if (!this.hasNewPatientSection) return how;
      return this.newPatientsSkipRules
        ? `New patients follow the section above and skip these rules. ${how}`
        : `Nothing is set up for new patients above, so they get these too. ${how}`;
    },
    // The classic "feature not enabled" notice, with its conditions and wording. `canFix`: Settings is where
    // the office sets this up, so the notice links there. The WhatsApp-only notice has nothing to fix.
    notice() {
      const b = this.wfBuilder;
      if (this.isEmail) {
        return b.emailFeatureEnabled
          ? null
          : { title: 'Email isn’t set up yet', text: 'This feature requires verified email addresses for your account.', canFix: true };
      }
      if (b.smsFeatureEnabled) return null;
      return b.smsOutboundOff
        ? { title: 'Texting is turned off', text: b.smsDisabledMessage, canFix: false }
        : { title: 'Texting isn’t set up yet', text: 'This feature requires SMS to be set up for your account.', canFix: true };
    },
    off() {
      return !!this.notice;
    },
    // Where "Open Settings" goes: the Settings page, as the builder page resolves it. Classic offers no link
    // on these notices, so there is no better destination. '' (no link) when the page has none.
    settingsHref() {
      const href = this.wfBuilder.settingsHref;
      return typeof href === 'string' ? href : '';
    },
    // The node being edited, measured by the Save gate's own library, live: { blocks, warnings }, each
    // keyed by rule index. Texts only (an email rule has no `smsText`).
    smsIssues() {
      const node = this.wfBuilder.selectedNodeData;
      if (this.isEmail || !node) return { blocks: {}, warnings: {} };
      const found = collectWorkflowSmsIssues([node]);
      return { blocks: byRule(found.blocks), warnings: byRule(found.warnings) };
    },
  },

  methods: {
    messageRuleSummary,
    newPatientRuleSummary,
    isRuleEmpty,
    ruleBlock(index) {
      return this.smsIssues.blocks[index] || null;
    },
    ruleWarning(index) {
      return this.smsIssues.warnings[index] || null;
    },
    // Under the rule's Template: the library's message, red for a blocking issue, amber for a warning.
    templateNote(index) {
      const block = this.ruleBlock(index);
      if (block) return block.message || TOO_LONG_FALLBACK;
      const warning = this.ruleWarning(index);
      return warning ? (warning.message || NEAR_LIMIT_FALLBACK) : '';
    },
    templateTone(index) {
      if (this.ruleBlock(index)) return 'red';
      return this.ruleWarning(index) ? 'amber' : '';
    },
    // What sits under the red / amber note beside Template: '' | 'clear' | 'pick'. A flagged rule that already
    // has a template can clear its old text; one without a template is told a template replaces it.
    noteExtra(index, rule) {
      if (!this.ruleBlock(index) && !this.ruleWarning(index)) return '';
      return rule && rule.templateId ? 'clear' : 'pick';
    },
    // Texts only: a text rule's saved `smsText` is dead data once the rule has a template (the sender uses the
    // template, and the text only when there is none), so it is dropped. A rule with no `smsText` key keeps
    // none, so an untouched rule saves byte-for-byte as it was. An email rule has no such text.
    clearOldText(rule) {
      if (this.isEmail || !rule || !Object.prototype.hasOwnProperty.call(rule, 'smsText')) return;
      this.$set(rule, 'smsText', '');
    },
    // The Template picker's @input: the classic `templateId` side effect, then (a template picked, not
    // cleared) the rule's old saved text goes. Clearing the template leaves the text alone.
    onTemplatePick(rule, val) {
      rule.templateId = val ? val[this.m.templateIdField] : '';
      if (val) this.clearOldText(rule);
    },
    addMessageRule() {
      this.addRuleAndOpen(() => this.rules, () => this.wfBuilder[this.m.add]());
    },
    addNpRule() {
      this.addRuleAndOpen(() => this.npRules, () => this.wfBuilder[this.m.addNp]());
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.ms {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ms__stack {
  display: flex;
  flex-direction: column;
  gap: 12px;

  // Classic's `feature-disabled`: seen, but not usable.
  &--off {
    opacity: 0.5;
    pointer-events: none;
  }
}

// A read-only value (the sender).
.ms__ro {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid $fu-border;
  border-radius: 10px;
  background: $fu-neutral-50;
  font-size: 14px;
  line-height: 1.4;
  color: $fu-neutral-700;
  overflow-wrap: anywhere;

  &--warn {
    border-color: $wf-amber-line;
    background: $fu-warning-subtle;
    color: #8a4a0b;
  }
}

.ms__ro-icon {
  color: $fu-neutral-400;

  .ms__ro--warn & { color: $fu-warning-text; }
}

.ms__choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 620px) { grid-template-columns: 1fr; }
}

.ms__choice {
  @include wf-choice-card;
  margin: 0;
}

.ms__choice-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ms__choice-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: $fu-text;
}

.ms__choice-desc {
  font-size: 12.5px;
  line-height: 1.4;
  color: $fu-neutral-500;
}

// The line between "For new patients only" and the rules for everyone else.
.ms__divider {
  height: 1px;
  margin: 12px 0 4px;
  background: $fu-border;
}

// Under the Template note of a flagged text rule: a sentence, or a small text button.
.ms__note-hint { display: block; }

.ms__note-btn {
  @include fu-link-btn;
  padding: 0 0 1px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;

  @include wf-touch { min-height: 44px; }
}

.ms__add {
  @include fu-btn(secondary, md);
  align-self: flex-start;

  @include wf-touch { height: 44px; }
}
</style>
